import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useHOA } from '../../contexts/HOAContext';
import { logEvent } from '../../lib/activityLogger';
import { clearInviteArrival, removeInviteTokenFromUrl } from '../../lib/inviteDetection';
import {
  buildLegalAcceptance,
  previewInvite,
  redeemInvite,
} from '../../services/inviteRedemptionService';
import { OnboardingForm } from './OnboardingForm';
import { InviteStatusCard } from './InviteStatusCard';

/**
 * App-owned invitation flow: /welcome?invite_token=<raw>.
 *
 * preview (read-only) decides what to show:
 *   ok                -> setup form; one POST to redeem-invite creates the
 *                        account, then the browser signs in with the password
 *                        that was just typed and continues to /portal.
 *   already_redeemed  -> this browser's session is that account: go to /portal.
 *   / active_account     another account is signed in: offer sign-out.
 *                        no session: "You're Already Set Up" (Sign In / Forgot).
 *   anything else     -> gentle "ask the board for a fresh invitation" card.
 *
 * Consent: when preview returns a `legal` block the form shows the agreement
 * checkboxes and redeem echoes the accepted document ids (legal_acceptance).
 * 400 consent_required / 409 legal_version_changed carry a fresh `legal`
 * block: the boxes reset, a notice explains why, typed fields are kept.
 */

const LEGAL_NOTICE_CHANGED =
  'The Terms of Use or Privacy Notice changed — please review and tick again';
const LEGAL_NOTICE_ACCEPT = 'Please accept to continue';
const LEGAL_NOTICE_UNAVAILABLE = "We couldn't load the Terms right now";

const isLegalRejection = (status, data) =>
  Boolean(data?.legal) &&
  ((status === 400 && data?.reason === 'consent_required') ||
    (status === 409 && data?.reason === 'legal_version_changed'));

const signInPath = (email) => `/?login=1&email=${encodeURIComponent(email || '')}`;
const resetPath = (email) => `/?reset=1&email=${encodeURIComponent(email || '')}`;

const Spinner = () => (
  <main className="min-h-screen bg-main-bg flex items-center justify-center" aria-busy="true">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary" />
    <span className="sr-only">Checking your invitation…</span>
  </main>
);

const getSessionUserId = async () => {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.user?.id || null;
  } catch (e) {
    return null;
  }
};

export const InviteTokenOnboarding = ({ token }) => {
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const { hoa } = useHOA();
  const mounted = useRef(true);
  // AuthContext/router callbacks are not guaranteed stable; keep the preview
  // effect keyed on the token only so it runs once per link.
  const actionsRef = useRef({ logout, navigate });
  actionsRef.current = { logout, navigate };

  const [view, setView] = useState('loading');
  const [info, setInfo] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [signingOut, setSigningOut] = useState(false);
  const [legalNotice, setLegalNotice] = useState('');
  const [retryingLegal, setRetryingLegal] = useState(false);

  const hoaName = info.hoa_name || hoa?.name || '';
  const supportEmail = info.support_email || hoa?.support_email || 'support@atrionos.com';

  const runPreview = useCallback(async () => {
    setView('loading');
    const sessionUserId = await getSessionUserId();
    const result = await previewInvite(token);
    if (!mounted.current) return;
    setInfo(result || {});
    setLegalNotice(result?.ok && result.legal_unavailable && !result.legal ? LEGAL_NOTICE_UNAVAILABLE : '');

    if (result?.ok) {
      // A valid, unused invitation while someone else is signed in on this
      // device (spouse, admin): sign that session out before setup.
      if (sessionUserId) {
        try {
          await actionsRef.current.logout();
        } catch (e) {
          await supabase.auth.signOut();
        }
      }
      if (mounted.current) setView('form');
      return;
    }

    if (result?.reason === 'already_redeemed' || result?.reason === 'active_account') {
      if (result.session_matches) {
        clearInviteArrival();
        actionsRef.current.navigate('/portal', { replace: true });
        return;
      }
      if (result.account_active === false) {
        setView('issue');
        return;
      }
      setView(sessionUserId ? 'other_session' : 'already');
      return;
    }

    setView(result?.reason === 'network' ? 'network' : 'issue');
  }, [token]);

  useEffect(() => {
    mounted.current = true;
    runPreview();
    return () => {
      mounted.current = false;
    };
  }, [runPreview]);

  // "Try again" after the Terms failed to load: re-preview without leaving the
  // form, so typed fields survive; anything unexpected falls back to a full preview.
  const retryLegal = async () => {
    setRetryingLegal(true);
    const result = await previewInvite(token);
    if (!mounted.current) return;
    setRetryingLegal(false);
    if (!result?.ok) {
      runPreview();
      return;
    }
    setInfo(result);
    setLegalNotice(result.legal_unavailable && !result.legal ? LEGAL_NOTICE_UNAVAILABLE : '');
  };

  const handleSubmit = async ({
    firstName,
    lastName,
    phone,
    password,
    agreedRequired,
    electronicNoticeGranted,
  }) => {
    setSubmitting(true);
    setSubmitError('');

    const legalAcceptance = buildLegalAcceptance(info.legal, {
      agreedRequired,
      electronicNoticeGranted,
    });
    const { status, data } = await redeemInvite({
      token,
      password,
      firstName,
      lastName,
      phone,
      legalAcceptance,
    });
    if (!mounted.current) return;

    if (data?.ok) {
      const email = data.email || info.email;
      try {
        await login(email, password);
      } catch (loginError) {
        // The account exists; let the person sign in normally.
        setInfo((current) => ({ ...current, email }));
        setSubmitting(false);
        setView('already');
        return;
      }
      logEvent('profile_updated', { source: 'invite_redeemed' });
      removeInviteTokenFromUrl();
      clearInviteArrival();
      navigate('/portal', { replace: true });
      return;
    }

    setSubmitting(false);
    if (isLegalRejection(status, data)) {
      // Nothing was created. Show the current documents and ask again.
      setInfo((current) => ({ ...current, legal: data.legal, legal_unavailable: false }));
      setLegalNotice(status === 409 ? LEGAL_NOTICE_CHANGED : LEGAL_NOTICE_ACCEPT);
      return;
    }
    if (status === 409 && ['already_redeemed', 'active_account'].includes(data?.reason)) {
      setInfo((current) => ({ ...current, email: data.email || current.email }));
      setView('already');
      return;
    }
    if (status === 410) {
      setView('issue');
      return;
    }
    setSubmitError(data?.error || 'Something went wrong. Please try again.');
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await logout();
    } finally {
      if (mounted.current) {
        setSigningOut(false);
        setView('already');
      }
    }
  };

  if (view === 'loading') return <Spinner />;

  if (view === 'form') {
    let consent = null;
    if (info.legal) {
      consent = { legal: info.legal, notice: legalNotice };
    } else if (info.legal_unavailable) {
      consent = {
        legal: null,
        unavailable: true,
        notice: legalNotice || LEGAL_NOTICE_UNAVAILABLE,
        onRetry: retryLegal,
        retrying: retryingLegal,
      };
    }
    return (
      <OnboardingForm
        hoaName={hoaName}
        email={info.email}
        propertyAddress={info.property_address}
        heroMessage="Your household is already approved. Choose a password below to finish."
        submitLabel="Activate My Access"
        submittingLabel="Setting up your account…"
        submitting={submitting}
        submitError={submitError}
        onSubmit={handleSubmit}
        consent={consent}
      />
    );
  }

  if (view === 'already') {
    return (
      <InviteStatusCard
        icon="how_to_reg"
        title="You're Already Set Up"
        body="Your portal account is ready. Sign in with the password you created."
        detail={info.email ? `Account: ${info.email}` : null}
        actions={[
          { label: 'Sign In', to: signInPath(info.email), variant: 'primary' },
          { label: 'Forgot your password?', to: resetPath(info.email), variant: 'link' },
        ]}
        footer={hoaName || null}
      />
    );
  }

  if (view === 'other_session') {
    return (
      <InviteStatusCard
        icon="switch_account"
        title="Signed In as Another Account"
        body="This invitation belongs to a different person. Sign out on this device so they can sign in with their own account."
        detail={info.email ? `Invitation for: ${info.email}` : null}
        actions={[
          {
            label: signingOut ? 'Signing out…' : 'Sign Out',
            onClick: handleSignOut,
            disabled: signingOut,
            variant: 'primary',
          },
          { label: 'Stay Signed In', to: '/portal', variant: 'link' },
        ]}
      />
    );
  }

  if (view === 'network') {
    return (
      <InviteStatusCard
        icon="wifi_off"
        title="We Couldn't Reach the Portal"
        body="Check your internet connection, then try again. Your invitation is still waiting for you."
        actions={[{ label: 'Try Again', onClick: runPreview, variant: 'primary' }]}
      />
    );
  }

  const superseded = info.reason === 'superseded';
  const requestHref = supportEmail
    ? `mailto:${supportEmail}?subject=${encodeURIComponent('Please send me a new portal invitation')}`
    : null;
  return (
    <InviteStatusCard
      icon={superseded ? 'forward_to_inbox' : 'mail'}
      title={superseded ? 'A Newer Invitation Was Sent' : "This Invitation Link Isn't Active"}
      body={
        superseded
          ? 'This link was replaced by a more recent invitation email. Please open the newest invitation from the board and use the button there.'
          : 'The board may have updated your invitation since this email was sent. Please ask them for a fresh invitation — it only takes a moment.'
      }
      actions={[
        ...(requestHref && !superseded
          ? [{ label: 'Request a New Invitation', href: requestHref, variant: 'primary' }]
          : []),
        { label: 'Return to Website', to: '/', variant: requestHref && !superseded ? 'link' : 'ghost' },
      ]}
    />
  );
};

export default InviteTokenOnboarding;
